/**
 * El hub de «Mi consultorio»: quiénes lo forman y cómo se reparte el pago.
 *
 * Nace del pedido del dueño (2-sep-2026): «no hay como un hub central donde
 * se vea mi consultorio, quiénes son los miembros, mi perfil, la parte de los
 * pagos». Crece DENTRO de la sección que ya existía en «Más» — el dueño pidió
 * explícitamente evitar una pestaña nueva, que sería redundante con la barra
 * de navegación.
 *
 * Los dos módulos de acá (miembros y reparto) son **solo lectura + pedido de
 * cambio**, nunca edición en vivo: quién entra o sale de un consultorio y
 * cómo se reparte la plata son decisiones de acceso y de dinero, y quien
 * decide sigue siendo Clean Blue. El médico puede actuar en el momento —pedir
 * el cambio desde acá—, pero el cambio en sí lo aplica el admin, por el mismo
 * camino que ya revisa (`service_requests`, categoría `SPECIAL_REQUEST`: es
 * la única que sobrevivió a la poda del 2-sep-2026 para un pedido que no es
 * limpieza, insumos ni emergencia).
 */

import { useState, type ReactNode } from 'react';
import qrcode from 'qrcode-generator';
import {
  ALLOCATION_MODE_HINT, ALLOCATION_MODE_LABEL, dateLabel, type AllocationMode,
} from '@cleanblue/shared';
import { api } from '@/lib/api';
import { useApiMutation } from '@/lib/hooks';
import { useSession } from '@/lib/session';
import { Badge, Button, Field, KeyValueRow, Modal, Money, Note } from '@/components/ui';

/* El rol de la membresía (`unit_members.role`), no el rol de la cuenta
   (`profiles.app_role`): acá interesa qué hace esta persona EN ESTE
   consultorio — quien ayuda con los pagos sin ser doctor también entra como
   «Administrador» (visión original del producto, 17-ago-2026). */
const UNIT_ROLE_LABEL: Record<string, string> = {
  MEMBER: 'Médico',
  UNIT_ADMIN: 'Administrador',
};

export interface ConsultorioMember {
  /** `unit_members.id`: a esto apunta `POST .../members/:memberId/remove`. */
  membership_id: string;
  profile_id: string;
  full_name: string;
  role: string;
}

export interface AllocationMemberInfo {
  display_name: string;
  percentage: number | null;
  fixed_amount_minor: string | null;
  rotation_seq: number | null;
}

export interface SubscriptionAllocation {
  subscription_id: string;
  service_name: string;
  allocation: {
    mode: AllocationMode;
    effective_from: string;
    rotation_anchor: string | null;
    members: AllocationMemberInfo[];
  } | null;
}

/* ── Miembros ──────────────────────────────────────────────────────────────── */

export function ConsultorioMembers({ unitId, unitCode, members }: {
  unitId: string; unitCode: string; members: ConsultorioMember[];
}) {
  const { user } = useSession();
  const [pidiendo, setPidiendo] = useState(false);
  const [invitando, setInvitando] = useState(false);
  const [quitando, setQuitando] = useState<ConsultorioMember | null>(null);

  /* Mismo alcance que exige el Worker (`assertCanAdminUnit`): ADMIN, o quien
     es UNIT_ADMIN de ESTE consultorio puntual (`admin_unit_ids`, no
     `unit_ids` — ser miembro no alcanza). No es la seguridad, esa la pone el
     Worker; es no ofrecerle a un médico común un botón que le va a
     responder 404. */
  const administraEsteConsultorio = user?.role === 'ADMIN' || (user?.admin_unit_ids.includes(unitId) ?? false);

  return (
    <>
      <div className="msec__title mt-2">Miembros del consultorio</div>
      <div className="mcard mcard--par">
        {members.length === 0 ? (
          <p className="small muted" style={{ padding: '10px 0' }}>Todavía no hay nadie registrado.</p>
        ) : (
          members.map((m, i) => (
            <KeyValueRow
              key={m.membership_id}
              label={m.full_name}
              style={i === members.length - 1 ? { borderBottom: 0 } : undefined}
            >
              <span className="row" style={{ gap: 8, justifyContent: 'flex-end' }}>
                <Badge tone={m.role === 'UNIT_ADMIN' ? 'info' : 'neutral'} plain>
                  {UNIT_ROLE_LABEL[m.role] ?? m.role}
                </Badge>
                {/* Nadie se saca a sí misma (regla del Worker, `cannot_remove_self`):
                    ni vale la pena ofrecer el botón sobre la propia fila. */}
                {administraEsteConsultorio && m.profile_id !== user?.profile_id && (
                  <Button variant="ghost" onClick={() => setQuitando(m)}>Quitar</Button>
                )}
              </span>
            </KeyValueRow>
          ))
        )}
      </div>

      {/* Agregar o quitar gente ya no pasa por Clean Blue (pedido del dueño,
          3-sep-2026): quien administra el consultorio invita por enlace o QR,
          y quita con doble confirmación. Lo que sigue pidiéndose acá es
          cualquier otra cosa. */}
      <div className="row row--wrap" style={{ marginTop: 8, gap: 8 }}>
        {administraEsteConsultorio && (
          <Button onClick={() => setInvitando(true)}>Invitar</Button>
        )}
        <Button onClick={() => setPidiendo(true)}>Pedir otro cambio en el equipo</Button>
      </div>

      {pidiendo && (
        <CambioModal
          titulo="Pedir otro cambio en el equipo"
          unitCode={unitCode}
          contexto={`Otro cambio en el equipo del consultorio ${unitCode}`}
          unitId={unitId}
          onClose={() => setPidiendo(false)}
        />
      )}

      {invitando && <InviteMemberModal unitId={unitId} onClose={() => setInvitando(false)} />}

      {quitando && (
        <RemoveMemberModal
          unitId={unitId}
          unitCode={unitCode}
          member={quitando}
          onClose={() => setQuitando(null)}
        />
      )}
    </>
  );
}

/* ── Invitar y quitar personas ─────────────────────────────────────────────── */

interface UnitInvite { invite_url: string; expires_at: string }

/**
 * La doble confirmación que pidió el dueño (3-sep-2026): «no solo un único
 * botón, sino doble confirmación». Escribir el nombre completo, no un
 * simple sí/no — nadie tipea por accidente el nombre de otra persona, y
 * copiarlo obliga a leerlo antes de confirmar.
 */
function RemoveMemberModal({ unitId, unitCode, member, onClose }: {
  unitId: string; unitCode: string; member: ConsultorioMember; onClose: () => void;
}) {
  const [nombre, setNombre] = useState('');
  const mutation = useApiMutation(
    () => api.post<{ ok: true }>(`/portal/units/${unitId}/members/${member.membership_id}/remove`),
    ['/portal/units'],
    { onSuccess: onClose },
  );

  const coincide = nombre.trim().toLowerCase() === member.full_name.trim().toLowerCase();

  return (
    <Modal
      title={`Quitar a ${member.full_name}`}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancelar</Button>
          <Button variant="danger" disabled={!coincide || mutation.isPending} onClick={() => mutation.mutate()}>
            {mutation.isPending ? 'Quitando…' : 'Quitar del consultorio'}
          </Button>
        </>
      }
    >
      {mutation.error && <Note tone="danger">{mutation.error.message}</Note>}
      <Note tone="warn">
        Sale del consultorio {unitCode} y pierde el acceso a sus datos y pagos. Sus cobros ya emitidos no se tocan.
      </Note>
      <Field label={`Escribí el nombre completo para confirmar: "${member.full_name}"`} required>
        <input className="input" value={nombre} onChange={(e) => setNombre(e.target.value)} autoFocus />
      </Field>
    </Modal>
  );
}

/**
 * El enlace y el QR se muestran juntos, no uno u otro — el dueño pidió
 * explícitamente las dos opciones (3-sep-2026: «con un link o con un QR,
 * ambas opciones»). Misma copia que `InviteModal` del admin (`Doctors.tsx`):
 * quien recibe la invitación entiende lo mismo sin importar por qué puerta
 * entró.
 */
function InviteMemberModal({ unitId, onClose }: { unitId: string; onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  const mutation = useApiMutation(() =>
    api.post<UnitInvite>(`/portal/units/${unitId}/invite`, { role: 'MEMBER' }));

  return (
    <Modal
      title="Invitar a alguien al consultorio"
      onClose={onClose}
      footer={mutation.data ? (
        <Button variant="primary" block onClick={onClose}>Listo</Button>
      ) : (
        <>
          <Button onClick={onClose}>Cancelar</Button>
          <Button variant="primary" disabled={mutation.isPending} onClick={() => mutation.mutate()}>
            {mutation.isPending ? 'Generando…' : 'Generar enlace'}
          </Button>
        </>
      )}
    >
      {mutation.error && <Note tone="danger">{mutation.error.message}</Note>}
      {mutation.data ? (
        <>
          <p className="small muted mb-2">
            Mandale este enlace o el código QR por WhatsApp o correo. Vence en una semana y sirve una sola vez.
          </p>
          <div className="row">
            <input
              className="input mono"
              readOnly
              value={mutation.data.invite_url}
              onFocus={(e) => e.target.select()}
            />
            <Button
              onClick={() => {
                void navigator.clipboard.writeText(mutation.data!.invite_url);
                setCopied(true);
              }}
            >
              {copied ? 'Copiado' : 'Copiar'}
            </Button>
          </div>
          <div className="mt-3" style={{ display: 'flex', justifyContent: 'center' }}>
            <InviteQrCode url={mutation.data.invite_url} />
          </div>
        </>
      ) : (
        /* Ya no se elige el rol: el enlace invita a un médico y nada más
           (8-set-2026). Antes había un desplegable con «Administrador», y como
           el enlace es un token al portador que se reenvía por WhatsApp, quien
           lo abriera podía invitar a más gente y sacar del consultorio al médico
           que lo generó. Para subir a alguien a administrador, se nos pide. */
        <p className="small muted">
          Se genera un enlace para que un médico entre a este consultorio. Vence
          en una semana y sirve una sola vez. Mandáselo solo a quien querés que
          entre: quien lo abra queda adentro.
        </p>
      )}
    </Modal>
  );
}

/**
 * QR dibujado en el navegador, sin canvas ni CDN: `qrcode-generator` da la
 * matriz de módulos y acá se pinta como SVG puro — un cuadrado por módulo
 * oscuro, sin dependencias de imagen externas.
 */
function InviteQrCode({ url }: { url: string }) {
  // `0` = versión automática según el largo del texto; `'M'` es la
  // corrección de errores por defecto de la librería, de sobra para un
  // enlace que se lee de cerca.
  const qr = qrcode(0, 'M');
  qr.addData(url);
  qr.make();
  const count = qr.getModuleCount();
  const cell = 4;
  const size = count * cell;

  const modulos: { x: number; y: number }[] = [];
  for (let fila = 0; fila < count; fila++) {
    for (let col = 0; col < count; col++) {
      if (qr.isDark(fila, col)) modulos.push({ x: col * cell, y: fila * cell });
    }
  }

  return (
    <svg viewBox={`0 0 ${size} ${size}`} width={168} height={168} role="img" aria-label="Código QR del enlace de invitación">
      <rect width={size} height={size} fill="#fff" />
      <g fill="#000">
        {modulos.map((m) => <rect key={`${m.x}-${m.y}`} x={m.x} y={m.y} width={cell} height={cell} />)}
      </g>
    </svg>
  );
}

/* ── Reparto de pago ──────────────────────────────────────────────────────── */

/** Cuánto le toca a un integrante, en la unidad en que está definido. */
function parteDe(m: AllocationMemberInfo, modo: AllocationMode): ReactNode {
  if (m.percentage != null) return `${m.percentage}%`;
  if (m.fixed_amount_minor != null) return <Money value={m.fixed_amount_minor} />;
  if (modo === 'ROTATION') return `Turno ${(m.rotation_seq ?? 0) + 1}`;
  // Ni porcentaje ni monto fijo en modo SPLIT: nadie fijó un valor, así que se
  // reparte por igual entre todos los integrantes — el mismo supuesto que usa
  // el motor de facturación (`domain/billing.ts`).
  return 'Partes iguales';
}

export function ConsultorioReparto({ unitId, unitCode, subscriptions }: {
  unitId: string; unitCode: string; subscriptions: SubscriptionAllocation[];
}) {
  const [pidiendo, setPidiendo] = useState(false);
  const conAsignacion = subscriptions.filter((s) => s.allocation);

  return (
    <>
      <div className="msec__title mt-2">Reparto de pago</div>
      {conAsignacion.length === 0 ? (
        <p className="small muted" style={{ padding: '10px 0 4px' }}>
          Todavía no hay un reparto definido para este consultorio.
        </p>
      ) : (
        conAsignacion.map((s) => (
          <div key={s.subscription_id} className="mcard mcard--par" style={{ marginBottom: 8 }}>
            <div className="mrow" style={{ paddingTop: 0 }}>
              <span className="mrow__l">
                <strong>{s.service_name}</strong>
                <span style={{ display: 'block', fontSize: 12, color: 'var(--muted)' }}>
                  Vigente desde {dateLabel(s.allocation!.effective_from)}
                </span>
              </span>
              <span className="mrow__r">
                <Badge tone="neutral" title={ALLOCATION_MODE_HINT[s.allocation!.mode]}>
                  {ALLOCATION_MODE_LABEL[s.allocation!.mode]}
                </Badge>
              </span>
            </div>
            {s.allocation!.members.map((m, i, arr) => (
              <KeyValueRow
                key={`${s.subscription_id}-${m.display_name}`}
                label={m.display_name}
                style={i === arr.length - 1 ? { borderBottom: 0 } : undefined}
              >
                {parteDe(m, s.allocation!.mode)}
              </KeyValueRow>
            ))}
          </div>
        ))
      )}
      {/* Solo lectura a propósito: el editor del admin (`Distribution.tsx`)
          tiene reglas de validación que no hay que duplicar mal en un rincón
          del portal, y es dinero — el default es «ver + pedir cambio», no
          editar en vivo (pedido del dueño, 2-sep-2026). */}
      <Button onClick={() => setPidiendo(true)}>Pedir cambio en el reparto</Button>

      {pidiendo && (
        <CambioModal
          titulo="Pedir cambio en el reparto"
          unitCode={unitCode}
          contexto={`Cambio en el reparto de pago del consultorio ${unitCode}`}
          unitId={unitId}
          onClose={() => setPidiendo(false)}
        />
      )}
    </>
  );
}

/* ── El modal de pedido, compartido por los dos módulos ───────────────────── */

/**
 * Un formulario simple que crea una `service_requests` con categoría
 * `SPECIAL_REQUEST` — la misma cola que ya revisa el admin (`ops.get
 * '/requests'`), reusada en vez de inventar una tabla nueva para esto. La
 * nota lleva SIEMPRE el contexto por delante («Cambio de miembros del
 * consultorio 601: …») para que se entienda de qué se trata sin abrir nada
 * más.
 *
 * Un solo campo de texto libre, siempre obligatorio: hasta el 3-sep-2026
 * este modal elegía entre tipos de cambio con botones (agregar/quitar
 * persona), pero esos dos ya tienen su propia acción self-service
 * (`InviteMemberModal`, `RemoveMemberModal`) — lo que queda acá es
 * cualquier OTRA cosa, y ahí no hay ningún botón que ya diga de qué se
 * trata.
 */
function CambioModal({ titulo, unitCode, contexto, unitId, onClose }: {
  titulo: string;
  unitCode: string;
  contexto: string;
  unitId: string;
  onClose: () => void;
}) {
  const [nota, setNota] = useState('');

  const mutation = useApiMutation(
    () => api.post<{ duplicate: boolean }>('/portal/requests', {
      service_unit_id: unitId,
      category: 'SPECIAL_REQUEST',
      note: `${contexto}: ${nota.trim()}`.slice(0, 500),
      priority: 'NORMAL',
    }),
    ['/portal/requests'],
  );

  const valido = nota.trim().length > 0;

  return (
    <Modal
      title={titulo}
      onClose={onClose}
      footer={mutation.isSuccess ? (
        <Button variant="primary" block onClick={onClose}>Cerrar</Button>
      ) : (
        <>
          <Button onClick={onClose}>Cancelar</Button>
          <Button variant="primary" disabled={!valido || mutation.isPending} onClick={() => mutation.mutate()}>
            {mutation.isPending ? 'Enviando…' : 'Enviar pedido'}
          </Button>
        </>
      )}
    >
      {mutation.error && <Note tone="danger">{mutation.error.message}</Note>}
      {mutation.isSuccess ? (
        <Note tone="ok">
          {mutation.data?.duplicate
            ? 'Ya teníamos tu pedido de hace un rato. No creamos otro.'
            : 'Listo, Clean Blue recibió tu pedido y te va a contactar.'}
        </Note>
      ) : (
        <>
          <p className="small muted mb-2">Consultorio {unitCode}</p>
          <Field label="Qué querés cambiar" required hint="Contanos qué necesitás y cómo debería quedar.">
            <textarea
              className="input"
              rows={3}
              maxLength={500}
              value={nota}
              onChange={(e) => setNota(e.target.value)}
              autoFocus
            />
          </Field>
        </>
      )}
    </Modal>
  );
}
